import { format, parse } from "date-fns"
import { CalendarIcon, Clock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { SleepStepContainer } from "../SleepStepContainer"
import { useSleepForm } from "../SleepFormContext"
import { formatLocalTimeWithTimezone } from "../utils/time"
import { useEffect } from "react"
import { toast } from "@/components/ui/use-toast"

interface TimeStepProps {
  type: "start" | "end"
  childName?: string
}

export function TimeStep({ type, childName }: TimeStepProps) {
  const form = useSleepForm()
  const timeValue = form.watch(`${type}_time`)
  const timeInputValue = form.watch(`${type}_time_input`)
  
  // For end_time validation, watch the start_time value
  const startTimeValue = form.watch('start_time')

  useEffect(() => {
    // Initialize with default values if no value exists
    if (!form.getValues('start_time')) {
      const now = new Date();
      form.setValue('start_time', now, { shouldValidate: true });
      
      // Set the time input as well
      const formattedNow = format(now, "HH:mm");
      form.setValue('start_time_input', formattedNow, { shouldValidate: true });
      
      console.log(`📝 [TimeStep.tsx] Initializing start time with current time:`, {
        now: now.toString(),
        formattedNow
      });
    }
    
    // Watch for changes to the start time to update the end time
    const subscription = form.watch((value, { name, type }) => {
      if (name === 'start_time' && type === 'change') {
        // This will call our existing end time update logic
        if (form.getValues('start_time')) {
          handleSyncEndTime();
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);

  // Initialize end time if needed (when end step is first shown)
  useEffect(() => {
    // Only run for the end time step
    if (type === 'end') {
      const currentEndTime = form.getValues('end_time');
      const startTime = form.getValues('start_time');
      
      // Check if we need to initialize end time (if start time exists and end time is not set or is invalid)
      if (startTime instanceof Date && 
          (!currentEndTime || !(currentEndTime instanceof Date) || currentEndTime <= startTime)) {
        
        // Create a new end time 30 minutes after start time
        const newEndTime = new Date(startTime);
        newEndTime.setMinutes(newEndTime.getMinutes() + 30);
        
        // Set the end time values
        form.setValue('end_time', newEndTime, { shouldValidate: true });
        
        // Also update the time input field - use consistent date-time format
        form.setValue('end_time_input', format(newEndTime, "HH:mm"), { shouldValidate: true });
        
        console.log(`📝 [TimeStep.tsx:30] Initializing end time based on start time:`, {
          startTime: startTime.toString(),
          newEndTime: newEndTime.toString(),
          formattedEndTime: format(newEndTime, "HH:mm")
        });
      }
    }
  }, [type, form]); // Run when the step type changes

  // Synchronize the time input value when the time value changes
  useEffect(() => {
    if (timeValue instanceof Date) {
      // Update the time input to match the time value
      const formattedTime = format(timeValue, "HH:mm");
      
      // Only update if different to prevent infinite loops
      if (formattedTime !== timeInputValue) {
        console.log(`📝 [TimeStep.tsx:26] Synchronizing ${type}_time_input based on ${type}_time:`, {
          timeValue: timeValue.toString(),
          formattedTime
        });
        form.setValue(`${type}_time_input`, formattedTime, { shouldValidate: true })
      }
    }
  }, [timeValue, form, type, timeInputValue])

  const handleSyncEndTime = () => {
    const startTime = form.getValues('start_time');
    const currentEndTime = form.getValues('end_time');
    
    // Check if we need to update end time (if start time exists and end time is not set or is invalid)
    if (startTime instanceof Date && 
        (!currentEndTime || !(currentEndTime instanceof Date) || currentEndTime <= startTime)) {
      
      // Create a new end time 30 minutes after start time
      const newEndTime = new Date(startTime);
      newEndTime.setMinutes(newEndTime.getMinutes() + 30);
      
      // Set the end time values
      form.setValue('end_time', newEndTime, { shouldValidate: true });
      
      // Also update the time input field - use consistent date-time format
      form.setValue('end_time_input', format(newEndTime, "HH:mm"), { shouldValidate: true });
      
      console.log(`📝 [TimeStep.tsx:30] Initializing end time based on start time:`, {
        startTime: startTime.toString(),
        newEndTime: newEndTime.toString(),
        formattedEndTime: format(newEndTime, "HH:mm")
      });
    }
  };

  // Handle time input change and ensure end time > start time validation
  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTimeInput = e.target.value;
    form.setValue(`${type}_time_input`, newTimeInput, { shouldValidate: true });
    
    // If we have a valid time format and a date selected, update the date object too
    if (newTimeInput.match(/^\d{1,2}:\d{2}$/) && timeValue instanceof Date) {
      const [hours, minutes] = newTimeInput.split(':').map(Number);
      const newDate = new Date(timeValue);
      newDate.setHours(hours);
      newDate.setMinutes(minutes);
      
      // IMPORTANT: For end time, validate that it's after start time
      if (type === 'end' && startTimeValue && newDate <= startTimeValue) {
        toast({
          variant: "destructive",
          title: "Invalid wake up time",
          description: "Wake up time must be after sleep time"
        });
        return; // Don't set the invalid value
      }
      
      // Set the updated date with the new time
      form.setValue(`${type}_time`, newDate, { shouldValidate: true });
      
      console.log(`📝 [TimeStep.tsx:NEW] Time input changed for ${type}_time:`, {
        newTimeInput,
        newDate: newDate.toString(),
        isValid: type !== 'end' || !startTimeValue || newDate > startTimeValue
      });
    }
  };

  // Use the current date for the calendar instead of hardcoded date
  const today = new Date();
  
  // For end step, force the calendar to use the same date as the start time
  const calendarDate = (type === 'end' && startTimeValue instanceof Date) 
    ? new Date(
        startTimeValue.getFullYear(),
        startTimeValue.getMonth(),
        startTimeValue.getDate()
      ) 
    : today;
  
  return (
    <SleepStepContainer>
      <div className="space-y-2">
        <h3 className="text-lg font-medium text-center">
          When did {childName || 'your baby'} {type === 'start' ? 'go to sleep' : 'wake up'}?
        </h3>
        <div className="flex flex-col gap-4 max-w-sm mx-auto">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                className="w-full justify-start text-left font-normal h-auto py-3"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {timeValue && timeValue instanceof Date ? format(timeValue, "PPP") : "Select a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center">
              <Calendar
                mode="single"
                selected={timeValue instanceof Date ? timeValue : calendarDate}
                defaultMonth={calendarDate}
                onSelect={(date) => {
                  if (date) {
                    // Create a new date object to avoid mutating the calendar's date
                    let dateToUse = new Date(date);
                    
                    // Get the current time input if it exists
                    const timeInput = form.getValues(`${type}_time_input`);
                    
                    // If we have a time input, apply it to the selected date
                    if (timeInput) {
                      const [hours, minutes] = timeInput.split(':').map(Number);
                      dateToUse.setHours(hours);
                      dateToUse.setMinutes(minutes);
                    } else {
                      // If no time input, use current time
                      const now = new Date();
                      dateToUse.setHours(now.getHours());
                      dateToUse.setMinutes(now.getMinutes());
                    }
                    
                    // For end_time, validate that the selected time is after start_time
                    if (type === 'end' && startTimeValue && dateToUse <= startTimeValue) {
                      // Simply ensure the end time is after start time
                      dateToUse = new Date(startTimeValue);
                      dateToUse.setMinutes(dateToUse.getMinutes() + 1);
                      
                      toast({
                        title: "Adjusted wake up time",
                        description: "Wake up time must be after sleep time."
                      });
                    }
                    
                    // Store the local date for UI display
                    form.setValue(`${type}_time`, dateToUse, { shouldValidate: true });
                    
                    // Log for debugging
                    console.log(`📝 [TimeStep.tsx:84] Calendar onSelect for ${type}_time:`, {
                      date: dateToUse.toString(),
                      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      offset: dateToUse.getTimezoneOffset(),
                      localDateString: dateToUse.toLocaleDateString()
                    });
                  }
                }}
                disabled={(date) => {
                  // Disable future dates
                  if (date > new Date()) return true;
                  
                  // For end_time: Also disable dates before or equal to start_time's date
                  if (type === 'end' && startTimeValue) {
                    // We need to compare just the dates (ignoring time)
                    const startDate = new Date(startTimeValue);
                    startDate.setHours(0, 0, 0, 0);
                    const compareDate = new Date(date);
                    compareDate.setHours(0, 0, 0, 0);
                    
                    // Allow same date selection for end_time (time validation happens in onSelect)
                    return compareDate < startDate;
                  }
                  
                  return false;
                }}
              />
            </PopoverContent>
          </Popover>

          <div className="relative">
            <Input
              id={`${type}-time-input`}
              type="time"
              placeholder="Enter time"
              value={timeInputValue || ''}
              onChange={handleTimeInputChange}
              className="mt-2"
            />
          </div>
        </div>
      </div>
    </SleepStepContainer>
  )
}
